import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/** Home-page featured video series grid. Fetches the full series list
 * (typically 8-16 folders) and displays a compact card for each so users
 * can jump straight into their favourite series without opening the whole
 * Video Library. Each card previews the series cover + episode count. */
const HomeFeaturedSeries = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customCovers, setCustomCovers] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [sRes, stateRes] = await Promise.all([
          fetch(`${API}/api/videos/featured-series`),
          fetch(`${API}/api/library/state?scope=videos`),
        ]);
        if (sRes.ok) {
          const sData = await sRes.json();
          // Feb-2026 (user): on the homescreen only ONE R' Shafier series
          // should appear — the primary Shmuz feed. WOW (World of Wisdom)
          // is kept as-is because it needs its globe cover.
          const allSeries = sData.series || [];
          const isShafier = (t) => /shafier/i.test(t || '');
          let shafierSeen = false;
          const filtered = allSeries.filter((s) => {
            if (isShafier(s.title)) {
              if (shafierSeen) return false;
              shafierSeen = true;
            }
            return true;
          });
          setSeries(filtered);
        }
        if (stateRes.ok) {
          const st = await stateRes.json();
          setCustomCovers(st.covers || {});
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || series.length === 0) return null;

  const goToSeries = (slug) => {
    navigate(`/videos?series=${encodeURIComponent(slug)}`);
  };

  return (
    <section className="mb-12" data-testid="home-featured-series">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#9a7b24] font-bold mb-1">Watch by</p>
          <h2 className="text-[#0f1729] dark:text-white text-2xl font-semibold">Video Series ({series.length})</h2>
        </div>
        <Button
          variant="ghost"
          className="hidden sm:inline-flex text-[#9a7b24] hover:text-[#0f1729] hover:bg-[#efede7] dark:hover:bg-white/5 dark:hover:text-white"
          onClick={() => navigate('/videos')}
          data-testid="home-videos-viewall"
        >
          Video library
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {series.map((s) => {
          // Feb-2026: pull from customCovers first (admin override) then
          // cover_image (portrait/first-video-thumb). Anything else falls
          // through to a blue+gold gradient with the series' initials so
          // we never render the browser's "?" broken-image glyph.
          const rawCover =
            customCovers[`series-${s.slug}`] ||
            s.cover_image ||
            s.cover_url ||
            (s.videos && (s.videos[0]?.thumbnailLink || s.videos[0]?.thumbnail));
          // Backend returns relative /api/portrait/... URLs for
          // portrait-backed covers; the browser needs the API origin
          // prepended so the <img loading="lazy" decoding="async" src> resolves against a non-relative
          // host (fixes "generic gold box" fallback that used to show
          // for every portrait-covered series).
          const cover = rawCover && typeof rawCover === 'string' && rawCover.startsWith('/')
            ? `${API}${rawCover}`
            : rawCover;
          const initials = (s.title || '').replace(/^R'?\s*/i, '').slice(0, 2).toUpperCase();
          return (
            <SeriesTile
              key={s.slug}
              slug={s.slug}
              title={s.title}
              cover={cover}
              videoCount={s.video_count}
              initials={initials}
              onOpen={() => goToSeries(s.slug)}
            />
          );
        })}
      </div>
    </section>
  );
};

/** Individual series tile with graceful cover fallback: if the primary
 *  cover fails to load (broken Drive thumb, private file, network hiccup)
 *  we swap to a blue→gold gradient with the series' initials, instead of
 *  the browser's default "?" broken-image glyph. */
const SeriesTile = ({ slug, title, cover, videoCount, initials, onOpen }) => {
  const [errored, setErrored] = useState(false);
  const showFallback = !cover || errored;
  return (
    <button
      onClick={onOpen}
      className="group relative aspect-square rounded-xl overflow-hidden bg-[#0f1729] shadow hover:shadow-lg hover:-translate-y-0.5 transition-all"
      data-testid={`home-series-${slug}`}
    >
      {showFallback ? (
        <div
          className="w-full h-full flex items-center justify-center text-white text-2xl font-bold"
          style={{ background: 'linear-gradient(135deg,#0f1729 0%,#1a2a4a 55%,#9a7b24 100%)' }}
        >
          {initials || <Play className="w-8 h-8 opacity-70" />}
        </div>
      ) : (
        <img
          src={cover}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setErrored(true)}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent flex flex-col justify-end p-2.5">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="w-6 h-6 rounded-full bg-[#9a7b24] flex items-center justify-center shadow group-hover:scale-110 transition-transform">
            <Play className="w-3 h-3 text-white ml-0.5" fill="currentColor" />
          </span>
          <span className="text-white/85 text-[10px] font-semibold tabular-nums">
            {videoCount} ep{videoCount === 1 ? '' : 's'}
          </span>
        </div>
        <div className="text-white text-sm font-bold leading-tight line-clamp-2">
          {title}
        </div>
      </div>
    </button>
  );
};

export default HomeFeaturedSeries;
